import React from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import './AlertCardMolecule.scss';

export type AlertVariant = 'info' | 'warning' | 'action' | 'success' | 'error';
export type AlertSize = 'compact' | 'normal' | 'large';

export interface AlertAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'text';
  icon?: string;
  disabled?: boolean;
}

export interface AlertCardMoleculeProps {
  // Banner section (optional)
  bannerTitle?: string;
  bannerSubtitle?: string;
  dismissible?: boolean;
  onDismiss?: () => void;

  // Main content
  title: string;
  description?: string;
  icon?: string;
  iconPosition?: 'left' | 'right';

  // Styling
  variant?: AlertVariant;
  size?: AlertSize;
  gradient?: boolean;

  // Actions
  actions?: AlertAction[];

  // Additional props
  className?: string;
  onClick?: () => void;
}

/**
 * AlertCardMolecule - Versatile alert/notification card component
 * Supports multiple variants (info, warning, action, success, error)
 * Optional dismissible banner, gradient backgrounds, and action buttons
 */
const AlertCardMolecule: React.FC<AlertCardMoleculeProps> = ({
  bannerTitle,
  bannerSubtitle,
  dismissible = false,
  onDismiss,
  title,
  description,
  icon,
  iconPosition = 'right',
  variant = 'info',
  size = 'normal',
  gradient = true,
  actions,
  className = '',
  onClick,
}) => {
  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDismiss) {
      onDismiss();
    }
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick();
    }
  };

  // Build CSS classes
  const cardClasses = [
    'alert-card-molecule',
    `alert-card--variant-${variant}`,
    `alert-card--size-${size}`,
    gradient ? 'alert-card--gradient' : '',
    onClick ? 'alert-card--clickable' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  // Render banner section
  const renderBanner = () => {
    if (!bannerTitle) return null;

    return (
      <div className="alert-card-banner">
        <div className="alert-card-banner-content">
          <div className="alert-card-banner-text">
            <h4 className="alert-card-banner-title">{bannerTitle}</h4>
            {bannerSubtitle && (
              <p className="alert-card-banner-subtitle">{bannerSubtitle}</p>
            )}
          </div>
          {dismissible && (
            <button
              className="alert-card-banner-dismiss"
              onClick={handleDismiss}
              aria-label="Dismiss alert"
              type="button"
            >
              <i className="pi pi-times" />
            </button>
          )}
        </div>
      </div>
    );
  };

  // Render main content
  const renderContent = () => {
    return (
      <div className="alert-card-content">
        <div className="alert-card-header">
          {icon && iconPosition === 'left' && (
            <i className={`pi ${icon} alert-card-icon alert-card-icon--left`} />
          )}
          <h3 className="alert-card-title">{title}</h3>
          {icon && iconPosition === 'right' && (
            <i className={`pi ${icon} alert-card-icon alert-card-icon--right`} />
          )}
        </div>
        {description && (
          <p className="alert-card-description">{description}</p>
        )}
      </div>
    );
  };

  // Render action buttons
  const renderActions = () => {
    if (!actions || actions.length === 0) return null;

    return (
      <div className="alert-card-actions">
        {actions.map((action) => (
          <Button
            key={action.label}
            label={action.label}
            icon={action.icon}
            onClick={action.onClick}
            disabled={action.disabled}
            className={`alert-card-action alert-card-action--${
              action.variant || 'primary'
            }`}
            outlined={action.variant === 'secondary'}
            text={action.variant === 'text'}
          />
        ))}
      </div>
    );
  };

  return (
    <div
      className={cardClasses}
      onClick={onClick ? handleCardClick : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(); } } : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {renderBanner()}
      <Card className="alert-card-main">
        {renderContent()}
        {renderActions()}
      </Card>
    </div>
  );
};

export default AlertCardMolecule;